import { z } from 'zod';

export const ScopeTypeSchema = z.enum(['SYSTEM', 'ORGANIZATION_GROUP', 'TENANT', 'PLACE']);

export type ScopeType = z.infer<typeof ScopeTypeSchema>;

export const UserAccessSummarySchema = z.object({
  scopeType: ScopeTypeSchema,
  organizationGroupId: z.uuid().nullable().optional(),
  tenantId: z.uuid().nullable().optional(),
  placeId: z.uuid().nullable().optional(),
  label: z.string().optional(),
  subtitle: z.string().optional(),
});

export type UserAccessSummary = z.infer<typeof UserAccessSummarySchema>;

export const ActiveContextSchema = z.object({
  scopeType: ScopeTypeSchema,
  organizationGroupId: z.uuid().nullable(),
  tenantId: z.uuid().nullable(),
  placeId: z.uuid().nullable(),
});

export type ActiveContext = z.infer<typeof ActiveContextSchema>;

export const LoginBodySchema = z.object({
  email: z.email(),
  password: z.string().min(8),
});

export type LoginBody = z.infer<typeof LoginBodySchema>;

export const RefreshTokenBodySchema = z.object({
  refreshToken: z.string().min(1).optional(),
});

export type RefreshTokenBody = z.infer<typeof RefreshTokenBodySchema>;

export const AuthSessionResponseSchema = z.object({
  accessToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
  tokenType: z.literal('Bearer'),
  accesses: z.array(UserAccessSummarySchema).optional(),
  activeContext: ActiveContextSchema.optional(),
});

export type AuthSessionResponse = z.infer<typeof AuthSessionResponseSchema>;

export const AuthMeResponseSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid().optional(),
  email: z.email().optional(),
  firstName: z.string().optional(),
  roles: z.array(z.string()),
  placeId: z.uuid().optional(),
  organizationGroupId: z.uuid().optional(),
  personKind: z.enum(['INDIVIDUAL', 'COMPANY']).optional(),
  accesses: z.array(UserAccessSummarySchema).optional(),
  activeContext: ActiveContextSchema.optional(),
});

export type AuthMeResponse = z.infer<typeof AuthMeResponseSchema>;

export const SwitchContextBodySchema = z.object({
  scopeType: ScopeTypeSchema,
  organizationGroupId: z.uuid().optional(),
  tenantId: z.uuid().optional(),
  placeId: z.uuid().optional(),
});

export type SwitchContextBody = z.infer<typeof SwitchContextBodySchema>;

export const SwitchContextResponseSchema = z.object({
  accessToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
  tokenType: z.literal('Bearer'),
  activeContext: ActiveContextSchema,
});

export type SwitchContextResponse = z.infer<typeof SwitchContextResponseSchema>;

export const WorkshopPlaceOptionSchema = z.object({
  placeId: z.uuid(),
  tradeName: z.string().min(1),
  active: z.boolean(),
});

export type WorkshopPlaceOption = z.infer<typeof WorkshopPlaceOptionSchema>;

export const WorkshopOrganizationOptionSchema = z.object({
  tenantId: z.uuid(),
  tradeName: z.string().min(1),
  places: z.array(WorkshopPlaceOptionSchema).min(1),
});

export type WorkshopOrganizationOption = z.infer<typeof WorkshopOrganizationOptionSchema>;

/** Workshop login must not pre-validate password length — always authenticate (401). */
export const WorkshopLoginBodySchema = z.object({
  email: z.email(),
  password: z.string(),
});
export type WorkshopLoginBody = z.infer<typeof WorkshopLoginBodySchema>;

export const WorkshopLoginResponseSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
  tokenType: z.literal('Bearer'),
  firstName: z.string().min(1),
  userId: z.uuid(),
  organizations: z.array(WorkshopOrganizationOptionSchema),
});

export type WorkshopLoginResponse = z.infer<typeof WorkshopLoginResponseSchema>;

export const WorkshopRefreshBodySchema = z.object({
  refreshToken: z.string().min(1),
  placeId: z.uuid(),
});

export type WorkshopRefreshBody = z.infer<typeof WorkshopRefreshBodySchema>;

export const WorkshopRefreshResponseSchema = WorkshopLoginResponseSchema.extend({
  placeId: z.uuid(),
  tenantId: z.uuid(),
});

export type WorkshopRefreshResponse = z.infer<typeof WorkshopRefreshResponseSchema>;

export const WorkshopForgotPasswordBodySchema = z.object({
  email: z.email(),
});

export type WorkshopForgotPasswordBody = z.infer<typeof WorkshopForgotPasswordBodySchema>;

export const WorkshopForgotPasswordResponseSchema = z.object({
  accepted: z.literal(true),
});

export type WorkshopForgotPasswordResponse = z.infer<typeof WorkshopForgotPasswordResponseSchema>;

export const WorkshopResetPasswordBodySchema = z.object({
  token: z.string().min(1),
  newPassword: z.string().min(8),
});

export type WorkshopResetPasswordBody = z.infer<typeof WorkshopResetPasswordBodySchema>;

export const WorkshopResetPasswordResponseSchema = z.object({
  accepted: z.literal(true),
});

export type WorkshopResetPasswordResponse = z.infer<typeof WorkshopResetPasswordResponseSchema>;

export const AcceptInviteBodySchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
});

export type AcceptInviteBody = z.infer<typeof AcceptInviteBodySchema>;

export const AcceptInviteResponseSchema = z.object({
  accepted: z.literal(true),
});

export type AcceptInviteResponse = z.infer<typeof AcceptInviteResponseSchema>;
